
import React, { useState, useEffect } from 'react';
import { getAuthStatus } from '@/lib/api/auth';
import { Loader2 } from 'lucide-react';
import { AuthLoginPage } from './AuthLoginPage';
import { toast } from 'sonner';

const DAEMON_WS_URL = 'ws://localhost:9998';

type AuthStatus = 'loading' | 'authorized' | 'unauthorized';

export const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');

  const checkStatus = async () => {
      try {
      const { status } = await getAuthStatus();
      if (status === 'authorized') {
        setAuthStatus('authorized');
      } else {
        setAuthStatus('unauthorized');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      toast.error(errorMessage);
      setAuthStatus('unauthorized'); // Assume unauthorized if daemon is not reachable for auth check
    }
  };

  useEffect(() => {
    checkStatus();
  }, []);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout;

    const connect = () => {
      ws = new WebSocket(DAEMON_WS_URL);

      ws.onopen = () => {
        // Connection established
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === 'auth_success') {
            window.location.reload();
          }
        } catch (error) {
          console.error('AuthGate: Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        // Simple backoff
        reconnectTimeout = setTimeout(connect, 3000);
      };

      ws.onerror = (error) => {
        console.error('AuthGate: WebSocket error:', error);
        ws?.close();
      };
    };

    connect();

    return () => {
      clearTimeout(reconnectTimeout);
      if (ws) {
        ws.onclose = null; // Prevent reconnection on component unmount
        ws.close();
      }
    };
  }, []);

  if (authStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="mt-4 text-lg text-muted-foreground">Checking authentication status...</p>
      </div>
    );
  }

  if (authStatus === 'unauthorized') {
    return <AuthLoginPage />;
  }

  return <>{children}</>;
};
